<template>
  <div class="form-builder assignment">
    <div>
      <h1>{{ assignmentLabel }}</h1>
    </div>
    <md-card class="assignment-card">
      <md-card-content>
        <div v-if="learnersOptions.length != 0">
          <h2>1. {{editMode ? 'Edit' : 'Create'}}</h2>
          <div class="assignment-section">
            <md-field :class="{'md-invalid': !this.valid && this.titleError}">
              <label>Title</label>
              <md-input v-model="title"></md-input>
              <span class="md-error">*Give your {{ assignmentLabel.toLowerCase() }} a title.</span>
            </md-field>
            <fb-rich-text
                    v-model="description"
                    :uuid="$uuid.v1()"
                    :mode="true"
                    :element="{data: {value: description}}"
                    editor_format="client_full_html"
            />
            <fb-file-view
                    :data-props="links"
                    @update-links="updateLinks"
                    @update-files="updateFiles"
                    @update-error="updateError(true)"
                    :nid="nid"
            />

            <div class="text-left" v-bind:key="index" v-for="(val, index) in links">
              <component
                      @delete="del(index, 'links')"
                      @upload-file="fileUploaded(index, 'links', $event)"
                      @upload-file-fail="fileUploadFail(index,'links', $event)"
                      :data-props="val"
                      :clean-error="cleanError"
                      upload-url="/jsonapi/assignment/assignment/attached_file"
                      @update-error="updateError(false)"
                      :is="val.type == 'link' ? 'FbFileViewLink' : 'FbFileViewFile'"
              />
            </div>
            <fb-video-view
                    :data-props="videos"
                    @update-files="updateFiles($event, 'videos')"
                    @update-error="updateError(true)"
                    :nid="nid"
            />
            <div class="text-left" v-bind:key="index" v-for="(val, index) in videos">
              <component
                      @delete="del(index, 'videos')"
                      @upload-file="fileUploaded(index, 'videos', $event)"
                      @upload-file-fail="fileUploadFail(index, 'videos', $event)"
                      :data-props="val"
                      :clean-error="cleanError"
                      upload-url="/jsonapi/assignment/assignment/attached_file"
                      @update-error="updateError(false)"
                      :is="'FbVideoViewFile'"
                      :sid="submissionUuid"
              />
            </div>
          </div>
          <h2>2. Assign to Learners</h2>
          <div class="assignment-section">
            <md-field
                    class="fb-select assignment-learner-select"
                    :class="{'md-invalid': !this.valid && this.learnerError}"
            >
              <md-select
                      ref="learnersSelect"
                      placeholder="- Select Learners -"
                      @md-selected="resetAllIfCount"
                      @md-closed="resetAll"
                      @md-opened="setAllClick"
                      id="select-view"
                      v-model="learners"
                      :multiple="true"
              >
                <md-option
                        @click.native="setAllLearners(1)"
                        ref="learnersOption"
                        value="all"
                >All Learners</md-option>
                <md-option
                        :key="learner.uuid"
                        v-for="learner in learnersOptions"
                        :value="learner.uuid"
                        :disabled="!!getLearnerSubmission(learner.uuid).data"
                >{{learner.name}}</md-option>
              </md-select>
              <span class="md-helper-text"></span>
            </md-field>
            <md-field
                    v-if="!this.valid && this.learnerError"
                    :class="{'md-invalid': !this.valid && this.learnerError}"
                    class="label-top"
            >
              <span class="md-error">*Select which learner(s) should receive this {{ assignmentLabel.toLowerCase() }}.</span>
            </md-field>
          </div>
          <h2>3. Set Learner Submission Requirements</h2>
          <div class="assignment-section">
            <div class="md-layout-item text-left">
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-toolbar-section-start">
                  <div class="label-top">
                    <md-checkbox
                            :disabled="submission_required_disabled"
                            v-model="submission_required"
                    >Learner submission required</md-checkbox>
                  </div>
                </div>
              </div>
            </div>
            <div class="assignment-date">
              <md-datepicker v-model="date" :required="true">
                <label>Select Due Date (optional)</label>
                <span class="md-helper-text"></span>
                <span class="md-error">*Please fill date.</span>
              </md-datepicker>
            </div>
          </div>
          <div class="md-layout-item md-layout md-alignment-center-center">
            <md-button v-if="showSaveDraft" class="md-button" @click="save">Save Draft</md-button>
            <md-button class="md-primary" @click="start">Save & Send</md-button>
          </div>
        </div>
        <div v-else class="md-layout">
          <h3>You do not have any learners assigned.</h3>
        </div>
      </md-card-content>
    </md-card>
    <spinner v-if="showLoader"></spinner>
  </div>
</template>

<script>
import HelpersMixin from "./HelpersMixin";
import Spinner from "./components/Spinner.vue";
import FbFileView from "./components/Form/FbFile/FbFileView";
import FbFileViewFile from "./components/Form/FbFile/FbFileViewFile.vue";
import FbFileViewLink from "./components/Form/FbFile/FbFileViewLink.vue";
import FbVideoViewFile from "./components/Form/FbVideo/FbVideoViewFile.vue";
import FbVideoView from "./components/Form/FbVideo/FbVideoView.vue";

export default {
  name: "Temporary",
  props: ["mode", "nid", "sid", "noTemplate", "editMode"],
  mixins: [HelpersMixin],
  computed: {
    valid() {
      if (!this.validate) {
        return true;
      }
      if (this.isDraft) {
        return this.title && this.title != "";
      }
      return this.title && this.title != "" && this.learners.length;
    },
    titleError() {
      return !this.title || this.title == "";
    },
    learnerError() {
      if (!this.isDraft) {
        return this.learners.length == 0;
      }
      return false;
    },
  },
  data() {
    return {
      submissionUuid: window.drupalSettings.assignment.uuid,
      uuid: null,
      isDraft: !!+window.drupalSettings.assignment.is_draft,
      showSaveDraft: !!+window.drupalSettings.assignment.is_draft,
      validate: false,
      learner: null,
      learners: [],
      learnersOptions: window.drupalSettings.user.learners || [],
      date: null,
      menuOpen: false,
      showLoader: false,
      description:
        (window.drupalSettings.assignment &&
          window.drupalSettings.assignment.description) ||
        "",
      title: "",
      links: [],
      videos: [],
      cleanError: false,
      submission_required: false,
      submission_required_disabled: false,
      assignmentLabel: window.drupalSettings.group_catch_config.labels.assignment_label || "Assignment",
    };
  },
  components: {
    Spinner,
    FbFileView,
    FbFileViewFile,
    FbFileViewLink,
    FbVideoViewFile,
    FbVideoView,
  },
  mounted() {
    this.$material.locale.dateFormat = "MM/dd/yyyy";
    if (this.editMode) {
      this.uuid = window.drupalSettings.assignment.uuid;
      let withData = false;
      this.learners = window.drupalSettings.assignment.learners
        ? Object.keys(window.drupalSettings.assignment.learners).map((key) => {
            let submission = this.getLearnerSubmission(key);
            if (submission.data) {
              withData = true;
            }
            return key;
          })
        : [];

      this.description = window.drupalSettings.assignment.description;
      this.title = window.drupalSettings.assignment.title;
      this.date = window.drupalSettings.assignment.due_date
        ? this.$moment(
            window.drupalSettings.assignment.due_date,
            "YYYY-MM-DD"
          ).format("MM/DD/YYYY")
        : null;
      this.submission_required =
        window.drupalSettings.assignment.submission_required == 1;
      if (withData) {
        this.submission_required = true;
        this.submission_required_disabled = true;
      }
      this.links = window.drupalSettings.assignment.links
        ? JSON.parse(window.drupalSettings.assignment.links)
        : [];
      this.videos = window.drupalSettings.assignment.videos
        ? JSON.parse(window.drupalSettings.assignment.videos)
        : [];
    }

    this.$el.querySelector('input.md-select-value').setAttribute('aria-label', 'Input Select Learners');
    this.$el.querySelector('input.md-input-fake').setAttribute('aria-label', 'Learners');
    this.$el.querySelector('select').setAttribute('aria-label', 'Select Learners');

    this.$el.querySelectorAll('select').forEach((item) => {
      if (!item.options.length) {
        item.append(document.createElement('option'));
      }
    });
  },
  methods: {
    getLearnerSubmission(uuid) {
      if (!window.drupalSettings.assignment.submissions) {
        return false;
      }
      let submissions = Object.keys(
        window.drupalSettings.assignment.submissions
      ).filter((submissionId) => {
        let submission =
          window.drupalSettings.assignment.submissions[submissionId];
        return submission.learner.uuid == uuid;
      });
      if (submissions.length) {
        return window.drupalSettings.assignment.submissions[submissions[0]];
      }
      return false;
    },
    resetAllIfCount() {
      let all = this.learners.indexOf("all");
      if (
        (this.learners.length == 1 && all === 0) ||
        event.target ==
          this.$refs["learnersOption"].$el.querySelector(
            ".md-checkbox-container"
          )
      ) {
        return;
      }
      if (
        all !== -1 &&
        this.learners.length - 1 != this.learnersOptions.length
      ) {
        this.learners.splice(all, 1);
      }
    },
    resetAll() {
      let all = this.learners.indexOf("all");
      if (all !== -1) {
        this.learners.splice(all, 1);
      }
    },
    setAllLearners(reverse) {
      let all = this.learners.indexOf("all");
      if (reverse && all !== -1) {
        this.learners = ["all"].concat(
          this.learnersOptions.map((el) => {
            return el.uuid;
          })
        );
      } else if (reverse) {
        this.learners = this.learnersOptions
          .filter((learner) => {
            return this.getLearnerSubmission(learner.uuid).data;
          })
          .map((learner) => {
            return learner.uuid;
          });
      }
    },
    setAllClick() {
      if (this.learners.length == this.learnersOptions.length) {
        this.learners.push("all");
      }
      this.$refs["learnersSelect"].$el;
      this.$refs["learnersOption"].$el
        .querySelector(".md-checkbox-container")
        .addEventListener("click", () => {
          this.setAllLearners(1);
          event.preventDefault();
        });
    },
    updateError(value) {
      this.cleanError = value;
    },
    updateLinks(links) {
      this.links = this.links.concat(
        links.map((el) => {
          return {
            link: el,
            type: "link",
          };
        })
      );
    },
    updateFiles(file, key) {
      if (!key) {
        key = "links";
      }
      this[key] = this[key].concat([
        {
          link: file.name,
          type: "file",
          buffer: file.buffer,
          name: file.name,
          uuid: "",
          uri: "",
          filemime: "",
        },
      ]);
    },
    fileUploaded(index, key, file) {
      if (!key) {
        key = "links";
      }
      this[key][index].name = file.name;
      this[key][index].uuid = file.uuid;
      this[key][index].uri = file.uri;
      this[key][index].filemime = file.filemime;
      this.$emit("validation", this.valid);
      this.$emit("set-value", this.value);
    },
    fileUploadFail(index, key) {
      if (!key) {
        key = "links";
      }
      this[key].splice(index, 1);
      this.$emit("validation", this.valid);
      this.$emit("set-value", this.value);
    },
    del(index, key) {
      if (!key) {
        key = "links";
      }
      this[key].splice(index, 1);
    },
    save() {
      this.validate = true;
      this.isDraft = true;
      if (!this.valid) {
        return;
      }
      this.showLoader = true;
      return this.sendAssignmentRequest(true, false)
        .then(() => {
          if (this.editMode) {
            //this.showLoader = false;
            //window.location.reload();
            //return;
          }
          localStorage.setItem("assignment_message_action", "draft");
          localStorage.setItem("assignment_message_title", this.title);

          window.location.href = "/assignments";
        })
        .catch((e) => {
          this.showLoader = false;
          // TODO: Add error you page
          throw new Error(e);
        });
    },
    start() {
      this.isDraft = false;
      this.validate = true;
      if (!this.valid) {
        return;
      }
      this.showLoader = true;
      return this.sendAssignmentRequest(false, true)
        .then(() => {
          if (this.editMode) {
            //this.showLoader = false;
            //window.location.reload();
            //return;
          }
          localStorage.setItem("assignment_message_action", "save");
          localStorage.setItem("assignment_message_title", this.title);

          window.location.href = "/assignments";
        })
        .catch((e) => {
          this.showLoader = false;
          // TODO: Add error you page
          throw new Error(e);
        });
    },
    sendAssignmentRequest(isDraft, isStarted) {
      return this.getToken().then((token) => {
        let fetch_headers = new Headers();
        fetch_headers.append("Content-Type", "application/vnd.api+json");
        fetch_headers.append("X-CSRF-Token", token);
        let method = "POST";
        if (this.editMode) {
          method = "PATCH";
        }
        let value = {
          date: this.date,
        };
        value.isDraft = isDraft;
        value.isStarted = isStarted;
        let files = this.links.filter((link) => {
          return link.type == "file";
        });
        files = files.concat(
          this.videos.filter((video) => {
            return video.type == "file";
          })
        );
        let a_data = {
          data: {
            type: "assignment--assignment",
            attributes: {
              links_files: JSON.stringify(this.links),
              videos: JSON.stringify(this.videos),
              is_draft: isDraft,
              due_date: this.date
                ? this.$moment(this.date).format("YYYY-MM-DD")
                : null,
              submission_required: this.submission_required,
              description: this.description,
              title: this.title,
            },
            relationships: {
              learner:
                this.learners.length === 0
                  ? null
                  : {
                      data: this.learners.map((learner) => {
                        return {
                          type: "user--user",
                          id: learner,
                        };
                      }),
                    },
              attached_file:
                files.length === 0
                  ? null
                  : {
                      data: files.map((file) => {
                        return {
                          type: "file--file",
                          id: file.uuid,
                        };
                      }),
                    },
            },
          },
        };
        let url = "/jsonapi/assignment/assignment";
        if (this.editMode) {
          url += "/" + this.uuid;
          a_data.data.id = this.uuid;
        }
        return fetch(url, {
          credentials: "include",
          method: method,
          headers: fetch_headers,
          body: JSON.stringify(a_data),
        })
          .then((response) => {
            return response.json();
          })
          .then((json) => {
            if (json && json.errors && json.errors.length) {
              throw new Error(json.errors[0].title);
            }
            return json;
          });
      });
    },
  },
};
</script>
<style lang="scss">
.assignment {
  font-size: 16px;
  font-weight: normal;
  p {
    font-size: 16px;
    font-weight: normal;
  }
  .upload-link {
    flex-direction: row-reverse;
    display: flex;
    align-items: flex-start;
    .md-button:first-child {
      margin-right: auto !important;
    }
  }
  h1 {
    font-size: 36px;
    line-height: 54px;
    color: $title-blue;
    text-align: left;
    margin-top: 0px;
  }
  .assignment-card {
    padding: 0px;
    .md-card-content {
      padding: 24px;
    }
    h2:first-child {
      margin-top: 0;
    }
  }
  .assignment-section {
    padding-left: 20px;
    padding-right: 20px;
  }
  h2 {
    color: rgb(60, 60, 60);
    font-family: $font-filson-soft;
    font-size: 20px;
    font-weight: bold;
    line-height: 30px;
    text-align: left;
  }
  .assignment-learner-select,
  .assignment-date {
    max-width: 300px;
  }
}
</style>
